import React, { useRef, useEffect, useState } from "react";

interface SiderailProps {
  children: React.ReactNode;
  collapsedContent?: React.ReactNode;
  stickyTriggerSelector: string;
  stickyStopSelector: string;
  className?: string;
  collapsedClassName?: string;
  initialTop?: number;
}

function Siderail({
  children,
  collapsedContent,
  stickyTriggerSelector,
  stickyStopSelector,
  className = "",
  collapsedClassName = "",
  initialTop = 0,
}: SiderailProps) {
  const siderailRef = useRef<HTMLDivElement>(null);
  const [stickyState, setStickyState] = useState<
    "initial" | "sticky" | "stopped"
  >("initial");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [initialPosition, setInitialPosition] = useState({ top: 0 });
  const [stopPosition, setStopPosition] = useState(0);
  const [fixedPosition, setFixedPosition] = useState({ left: 0, width: 0 });

  useEffect(() => {
    const siderail = siderailRef.current;
    if (!siderail) return;

    const updateInitialPositions = () => {
      const rect = siderail.getBoundingClientRect();
      setInitialPosition({
        top: rect.top + window.scrollY,
      });
      setFixedPosition({
        left: rect.left,
        width: rect.width,
      });
    };

    updateInitialPositions();

    const handleScroll = () => {
      if (!siderail) return;

      const stickyTrigger = document.querySelector(stickyTriggerSelector);
      const stickyStop = document.querySelector(stickyStopSelector);

      if (!stickyTrigger || !stickyStop) {
        console.error(
          `Bound elements not found for : ${!stickyTrigger ? stickyTriggerSelector : stickyStopSelector}`,
        );
        return;
      }

      // Get current positions
      const triggerRect = stickyTrigger.getBoundingClientRect();
      const stopRect = stickyStop.getBoundingClientRect();
      const siderailRect = siderail.getBoundingClientRect();

      setFixedPosition({
        left: siderailRect.left,
        width: siderailRect.width,
      });

      // Use initialTop as the offset when sticky
      const headerOffset = initialTop;

      // Calculate where the siderail would stop
      const siderailHeight = siderailRect.height;
      const stopTop = stopRect.top + window.scrollY - siderailHeight;

      setStopPosition(stopTop);

      const triggerTop = triggerRect.top + window.scrollY;
      const scrolledToTrigger = window.scrollY >= triggerTop - headerOffset;

      const reachedStop = window.scrollY >= stopTop - headerOffset;

      if (reachedStop) {
        setStickyState("stopped");
      } else if (scrolledToTrigger) {
        setStickyState("sticky");
      } else {
        setStickyState("initial");
      }

      // If stickyState is 'sticky' or 'stopped', then isCollapsed should be true
      setIsCollapsed(scrolledToTrigger || reachedStop);
    };

    const handleResize = () => {
      // Don't recalculate - keep the locked position from initial load
      handleScroll();
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [stickyTriggerSelector, stickyStopSelector, initialTop]);

  useEffect(() => {
    // This effect ensures isCollapsed is always in sync with stickyState
    setIsCollapsed(stickyState !== "initial");
  }, [stickyState]);

  // Apply viewport buffer only to final positioning when stopped
  const viewportBuffer = window.innerHeight;
  const finalTopWhenStopped =
    stopPosition - initialPosition.top + initialTop - viewportBuffer;

  const style = {
    top:
      stickyState === "sticky"
        ? `${initialTop}px`
        : stickyState === "stopped"
          ? `${finalTopWhenStopped}px`
          : `${initialTop}px`,
    ...(stickyState === "sticky"
      ? {
          left: `${fixedPosition.left}px`,
          width: `${fixedPosition.width}px`,
        }
      : {}),
  };

  return (
    <div
      ref={siderailRef}
      className={`
        ${stickyState === "initial" ? "absolute" : stickyState === "sticky" ? "fixed" : "absolute"}
        z-30
        ${className}
        ${isCollapsed ? collapsedClassName : ""}
      `}
      style={style}
    >
      {isCollapsed && collapsedContent ? collapsedContent : children}
    </div>
  );
}

export default Siderail;
